import { Box, CircularProgress, Container, Grid, Paper, Typography } from '@mui/material';
import { useAdminLeadsVoDashboard } from '../../../hooks/useLeadsVoQueries';

const StatCard = ({
  title,
  value,
  subtitle,
  accent = false,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  accent?: boolean;
}) => (
  <Paper
    elevation={3}
    sx={{
      p: 3,
      borderRadius: 2,
      borderLeft: accent ? '4px solid #FDBB5F' : '4px solid transparent',
      height: '100%',
      minHeight: 120,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }}
  >
    <Typography sx={{ color: '#464646', fontWeight: 500, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
      {title}
    </Typography>
    <Typography sx={{ color: '#262626', fontWeight: 900, fontSize: 32, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography sx={{ color: '#9E9E9E', fontSize: 13, mt: 0.5 }}>
        {subtitle}
      </Typography>
    )}
  </Paper>
);

const Dashboard = () => {
  const { data: stats, isLoading } = useAdminLeadsVoDashboard();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#FDBB5F' }} />
      </Box>
    );
  }

  if (!stats) return null;

  const noShowRate =
    stats.rdvs.completed_rdvs + stats.rdvs.no_show_rdvs > 0
      ? ((stats.rdvs.no_show_rdvs / (stats.rdvs.completed_rdvs + stats.rdvs.no_show_rdvs)) * 100).toFixed(1)
      : '0';

  const hasData =
    stats.agents.total_agents > 0 || stats.packs.total_packs > 0 || stats.rdvs.total_rdvs > 0;

  return (
    <Container maxWidth={false} sx={{ py: 4, px: 4 }}>
      <Typography sx={{ fontWeight: 900, fontSize: 28, color: '#262626', mb: 4 }}>
        Dashboard RDV Qualifiés
      </Typography>

      {stats.rdvs_source === 'local' && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: '#FFF4E0', color: '#946200' }}>
          <Typography sx={{ fontSize: 14 }}>
            Le partenaire est injoignable : les chiffres affichés proviennent des données locales et peuvent être
            incomplets.
          </Typography>
        </Paper>
      )}

      {!hasData ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#262626', mb: 1 }}>
            Aucune donnée pour le moment
          </Typography>
          <Typography sx={{ color: '#464646' }}>
            Activez des agents et commandez des packs pour voir les statistiques ici.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Agents actifs" value={stats.agents.active_agents} subtitle={`${stats.agents.total_agents} au total · ${stats.agents.onboarding_agents} en onboarding`} accent />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Packs actifs" value={stats.packs.active_packs} subtitle={`${stats.packs.ordered_packs} commandés · ${stats.packs.closed_packs} terminés`} accent />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="RDV en attente"
              value={stats.rdvs.pending_rdvs}
              subtitle={`${stats.rdvs.proposed_rdvs} proposés · ${stats.rdvs.confirmed_rdvs} confirmés`}
              accent
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="RDV complétés" value={stats.rdvs.completed_rdvs} subtitle={`${stats.rdvs.total_rdvs} au total`} accent />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Taux no-show" value={`${noShowRate}%`} subtitle={`${stats.rdvs.no_show_rdvs} no-shows confirmés`} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Revenue total HT" value={`${Math.round(stats.packs.total_revenue_ht ?? 0).toLocaleString('fr-FR')}€`} accent />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="RDV annulés" value={stats.rdvs.cancelled_rdvs ?? 0} subtitle="Agence + particulier" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Packs commandés" value={stats.packs.ordered_packs} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Packs terminés" value={stats.packs.closed_packs} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Total agents" value={stats.agents.total_agents} />
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Dashboard;
